import {Component, Input, OnInit, ViewChild} from '@angular/core';
import {UserAccount} from '../../api/model/user-account';
import {NgbActiveModal} from '@ng-bootstrap/ng-bootstrap';
import {AccountService} from '../../api/resources/account/account.service';
import {UserAccountType} from '../../api/model/user-account-type';
import {ToasterService} from '../../global/toaster.service';
import {AuthenticationService} from '../../api/authentication/authentication.service';
import {Subscription} from 'rxjs/Subscription';
import { UserService } from '../../api/resources/user/user.service';
import { ImageSelectEvent } from '../../atoms/image-upload/image-upload.component';

@Component({
  selector: 'app-user-details',
  templateUrl: './user-details.component.html',
  styleUrls: ['./user-details.component.scss']
})
export class UserDetailsComponent implements OnInit {
  @Input('userAccount') userAccount!: UserAccount;
  @Input('mode') mode = 'edit';
  @ViewChild('userImageUpload') userImageUpload!: any;

  actionInProgress = false;
  showInlineInvalid = false;
  accessOptions = [
    {
      label: 'Basic User',
      value: 0
    },
    {
      label: 'Admin',
      value: 1
    }
  ];
  isEmailFreeSub!: Subscription;
  isFormValid = false;
  emailDirty = false;
  isEmailFree = false;
  isEditingSelfAdmin = false;
  isCurrentUserAdmin = false;
  currentUserId!: number;
  imageChanged: boolean = false;  // Track whether a new image was uploaded in the modal
  private baseline: { name?: string; isActive?: boolean; accessType?: number | null; imagePhoto?: string | null } = {}; // Baseline snapshot to detect whether anything changed (edit mode)

  constructor(
    public activeModal: NgbActiveModal,
    private _accountService: AccountService,
    private _toasterService: ToasterService,
    private _authService: AuthenticationService,
    private _userService: UserService
  ) { }

  ngOnInit() {
    const currentUser = this._userService.user;

    if (!this.userAccount) {
      this.userAccount = new UserAccount();
      this.userAccount.isActive = true;
      this.userAccount.userAccountTypeDefault = new UserAccountType();
      this.userAccount.userAccountTypeDefault.isDefault = true;
      this.userAccount.userAccountTypeDefault.accessType = 1;
      this.userAccount.userAccountTypes = [];
      this.userAccount.userAccountTypes.push(this.userAccount.userAccountTypeDefault);
      this.mode = 'create';
    }

    if (this.mode === 'edit') {
      this.snapshotBaseline();
    }

    if (
      currentUser &&
      this.userAccount &&
      this.userAccount.id === currentUser.id &&
      this.userAccount.userAccountTypeDefault.accessType === 1
    ) {
      this.isEditingSelfAdmin = true;
    }

    if (currentUser) {
      this.currentUserId = currentUser.id;
      this.isCurrentUserAdmin = currentUser.userAccountTypeDefault && currentUser.userAccountTypeDefault.accessType === 1;
    }

    // Compute initial validity so Save is disabled when there are no changes
    this.checkValidity();
  }

  createUser() {
    if (this.userImageUpload && this.userImageUpload.isImageValid === false) {
      this.openInvalidImageDialog();

      return;
    }

    this.actionInProgress = true;
    this._accountService.userAccountCreate(this.userAccount).subscribe({
      next: () => {
        this._toasterService.showInfo('Account created successfully.');
        this.activeModal.close();
      },
      error: () => {
        this.actionInProgress = false;
      },
      complete: () => {
        this.actionInProgress = false;
      }
    });
  }

  editUser() {
    if (this.userImageUpload && this.userImageUpload.isImageValid === false) {
      this.openInvalidImageDialog();
      
      return;
    }
    this.actionInProgress = true;
    this._accountService.userAccountEdit(this.userAccount).subscribe(
      (userAccount: UserAccount) => {
        this._toasterService.showInfo('Account edited successfully.');
        this.activeModal.close();
      },
      undefined,
      () => {
        this.actionInProgress = false;
      }
    );
  }

  deleteUser() {
    this.actionInProgress = true;
    this._accountService.userAccountDelete(this.userAccount).subscribe({
      next: () => {
        this._toasterService.showInfo('Account deleted successfully.');
        this.activeModal.close();
      },
      error: () => {
        this.actionInProgress = false;
      },
      complete: () => {
        this.actionInProgress = false;
      }
    });
  }

  getOriginalDefaultAccountType(): UserAccountType {
    let result = new UserAccountType();

    if (this.userAccount && this.userAccount.userAccountTypes) {
      for (const userAccountType of this.userAccount.userAccountTypes) {
        if (userAccountType.isDefault) {
          result = userAccountType;
          break;
        }
      }
    }

    return result;
  }

  checkValidity() {
    if (
      (this.mode === 'create' &&
      !this.isNameEmpty() &&
      !this.isEmailEmpty() && this.isEmailValid() && this.isEmailFree) ||
      (this.mode === 'edit' && !this.isNameEmpty() && this.hasChanges())
    ) {
      this.isFormValid = true;
    } else {
      this.isFormValid = false;
    }
  }

  private snapshotBaseline() {
    this.baseline = {
      name: this.userAccount && this.userAccount.name ? this.userAccount.name.trim() : '',
      isActive: !!(this.userAccount && this.userAccount.isActive),
      accessType: this.getOriginalDefaultAccountType() ? this.getOriginalDefaultAccountType().accessType : null,
      imagePhoto: this.userAccount ? (this.userAccount.imagePhoto || null) : null
    };
  }

  private hasChanges(): boolean {
    if (!this.baseline) return true;
    
    const nameNow = this.userAccount && this.userAccount.name ? this.userAccount.name.trim() : '';
    if (nameNow !== (this.baseline.name || '')) return true;
    
    const activeNow = !!(this.userAccount && this.userAccount.isActive);
    if (activeNow !== !!this.baseline.isActive) return true;
    
    const accessNow = this.getOriginalDefaultAccountType() ? this.getOriginalDefaultAccountType().accessType : null;
    if (accessNow !== (this.baseline.accessType as any)) return true;
    
    // Consider image changed only if flagged true or the image URL differs
    if (this.imageChanged) return true;
    
    const imgNow = this.userAccount ? (this.userAccount.imagePhoto || null) : null;
    if (imgNow !== (this.baseline.imagePhoto || null)) return true;
    
    return false;
  }

  isNameEmpty(): boolean {
    return !this.userAccount.name || this.userAccount.name === '';
  }

  onNameChange(newVal: string) {
    // Ensure model is updated before checking validity
    if (this.userAccount) {
      this.userAccount.name = newVal;
    }
    this.checkValidity();
  }

  onImageSelected(evt: ImageSelectEvent) {
    if (evt.base64) {
      (this.userAccount as any).imagePhotoBase64 = evt.base64;
    }
    this.imageChanged = !!evt.changed || (this.userImageUpload && this.userImageUpload.isImageValid === false);
    this.checkValidity();
  }

  isEmailEmpty(): boolean {
    return !this.userAccount.email || this.userAccount.email === '';
  }

  isEmailValid() {
    const re = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9-]+(\.[a-zA-Z0-9-]+)*\.[a-zA-Z]{2,}$/;
    return re.test(this.userAccount.email);
  }

  onEmailInput() {
    if (!this.isEmailEmpty() && this.isEmailValid()) {
      if (this.isEmailFreeSub) {
        this.isEmailFreeSub.unsubscribe();
      }
      this.isEmailFreeSub = this._authService.isEmailFree(this.userAccount.email).subscribe(
        (res: any) => {
          this.isEmailFree = res.isSuccessStatusCode;
          this.emailDirty = true;
          this.checkValidity();
        }
      );
    } else {
      this.emailDirty = true;
      this.checkValidity();
    }
  }
  
  private openInvalidImageDialog() {
    this.showInlineInvalid = true;
  }
}



// WEBPACK FOOTER //
// ./src/app/shared/atoms/user-details/user-details.component.ts